import { Avatar, ButtonBase, AvatarProps } from "@mui/material";
import { ChangeEvent } from "react";

export type TAvatarUploadError = "file-too-large" | "unknown-error";

type TAvatarUploadProps = AvatarProps & {
  src?: string | undefined;
  onFileChange?: (src: string, file: File) => void;
  accept?: string;
  maxFileSize?: number; // in bytes
  onUploadError?: (error: TAvatarUploadError) => void;
  disabled?: boolean;
};

export const AvatarUpload = ({
  src,
  alt,
  onFileChange,
  maxFileSize,
  onUploadError,
  disabled,
  accept = "image/*",
  ...restProps
}: TAvatarUploadProps) => {
  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (maxFileSize && file.size > maxFileSize) {
        onUploadError?.("file-too-large");
        return;
      }

      // Read the file as a data URL
      const reader = new FileReader();
      reader.onload = () => {
        onFileChange?.(reader.result as string, file);
      };
      reader.onerror = () => {
        onUploadError?.("unknown-error");
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <ButtonBase
      component="label"
      role={undefined}
      disabled={disabled}
      tabIndex={-1} // prevent label from tab focus
      sx={{
        borderRadius: "50%",
        "&:has(:focus-visible)": {
          outline: "2px solid",
          outlineOffset: "2px",
        },
      }}
    >
      <Avatar alt={alt} src={src} {...restProps} />
      <input
        type="file"
        value=""
        accept={accept}
        style={{
          border: 0,
          clip: "rect(0 0 0 0)",
          height: "1px",
          margin: "-1px",
          overflow: "hidden",
          padding: 0,
          position: "absolute",
          whiteSpace: "nowrap",
          width: "1px",
        }}
        onChange={handleAvatarChange}
      />
    </ButtonBase>
  );
};

export default AvatarUpload;
